<template>
  <div v-if="images.length > 0">
    <v-btn
      icon
      x-small
      @click="openLightbox">
      <v-icon small>
        mdi-information-outline
      </v-icon>
    </v-btn>

    <v-dialog
      v-model="dialog"
      max-width="900">
      <v-card>
        <v-toolbar
          flat
          dense>
          <v-btn
            v-if="viewerOpen"
            icon
            @click="showGrid">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <v-toolbar-title class="subtitle-1 font-weight-medium">
            {{ dialogTitle }}
          </v-toolbar-title>
          <v-spacer />
          <v-btn
            icon
            @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-divider />

        <!-- Grid overview -->
        <v-card-text
          v-if="!viewerOpen"
          class="pa-4">
          <div class="help-grid">
            <div
              v-for="(img, i) in images"
              :key="i"
              class="help-cell"
              @click="openViewer(i)">
              <v-img
                :src="img.src"
                :aspect-ratio="16 / 9" />
              <div class="help-caption">
                {{ img.caption }}
              </div>
            </div>
          </div>
        </v-card-text>

        <!-- Full-image viewer -->
        <div
          v-else
          class="viewer-wrap">
          <div class="viewer-image-area">
            <v-btn
              icon
              dark
              class="nav-arrow nav-prev"
              :disabled="currentIndex === 0"
              @click="navigate(-1)">
              <v-icon large>
                mdi-chevron-left
              </v-icon>
            </v-btn>
            <v-img
              :src="currentImage.src"
              contain
              height="60vh" />
            <v-btn
              icon
              dark
              class="nav-arrow nav-next"
              :disabled="currentIndex === images.length - 1"
              @click="navigate(1)">
              <v-icon large>
                mdi-chevron-right
              </v-icon>
            </v-btn>
          </div>
          <div class="viewer-footer">
            <span class="viewer-caption">{{ currentImage.caption }}</span>
            <div class="viewer-dots">
              <div
                v-for="(_, i) in images"
                :key="i"
                class="viewer-dot"
                :class="{ 'viewer-dot--active': i === currentIndex }"
                @click="goTo(i)" />
            </div>
            <span class="viewer-counter">{{ currentIndex + 1 }} / {{ images.length }}</span>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import imgProductDetail from '@/assets/image/pre-order-tooltip/gw-product-detail.png'
import imgPopup from '@/assets/image/pre-order-tooltip/gw-popup.png'
import imgCart from '@/assets/image/pre-order-tooltip/gw-cart.png'
import imgCheckout from '@/assets/image/pre-order-tooltip/gw-checkout.png'
import imgBill from '@/assets/image/pre-order-tooltip/gw-bill.png'
import imgMail from '@/assets/image/pre-order-tooltip/gw-mail.png'
import imgMordred from '@/assets/image/pre-order-tooltip/mordred.png'

const IMAGES = [
  { src: imgProductDetail, caption: 'Product Detail Page' },
  { src: imgPopup, caption: 'Product Quick View' },
  { src: imgCart, caption: 'Shopping Cart' },
  { src: imgCheckout, caption: 'Checkout Page' },
  { src: imgBill, caption: 'Order Details' },
  { src: imgMail, caption: 'Order Confirmation Email' },
  { src: imgMordred, caption: 'Order Management (Internal)' }
]

export default {
  name: 'PreOrderNoteHelp',
  data () {
    return {
      dialog: false,
      viewerOpen: false,
      currentIndex: 0,
      images: IMAGES
    }
  },
  computed: {
    currentImage () {
      return this.images[this.currentIndex]
    },
    dialogTitle () {
      return this.viewerOpen
        ? this.currentImage.caption
        : 'Where does Pre-Order Note appear?'
    }
  },
  watch: {
    dialog (val) {
      if (!val) this.viewerOpen = false
    }
  },
  methods: {
    openLightbox () {
      this.viewerOpen = false
      this.dialog = true
    },
    openViewer (i) {
      this.currentIndex = i
      this.viewerOpen = true
    },
    showGrid () {
      this.viewerOpen = false
    },
    navigate (dir) {
      const next = this.currentIndex + dir
      if (next >= 0 && next < this.images.length) {
        this.currentIndex = next
      }
    },
    goTo (i) {
      this.currentIndex = i
    }
  }
}
</script>

<style scoped>
.help-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.help-cell {
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  transition: box-shadow 0.15s, transform 0.15s;
}

.help-cell:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  transform: translateY(-2px);
}

.help-caption {
  background: #212121;
  color: #fff;
  font-size: 12px;
  text-align: center;
  padding: 7px 12px;
}

.viewer-wrap {
  background: #1a1a1a;
}

.viewer-image-area {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 64px;
  background: #1a1a1a;
}

.nav-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.1) !important;
}

.nav-arrow:hover {
  background: rgba(255, 255, 255, 0.2) !important;
}

.nav-prev { left: 12px; }
.nav-next { right: 12px; }

.viewer-footer {
  background: #212121;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 24px;
}

.viewer-caption {
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  flex: 1;
}

.viewer-counter {
  color: rgba(255, 255, 255, 0.55);
  font-size: 12px;
  flex: 1;
  text-align: right;
}

.viewer-dots {
  display: flex;
  gap: 6px;
  align-items: center;
}

.viewer-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  cursor: pointer;
  transition: background 0.15s, transform 0.15s;
}

.viewer-dot--active {
  background: #fff;
  transform: scale(1.3);
}
</style>
